import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { MultiselectDropdownComponent } from '../../../../shared/components/multiselect-dropdown/multiselect-dropdown.component';
import { PageHeaderComponent } from '../../../../shared/components/page-header/page-header.component';
import { SkeletonLoaderComponent } from '../../../../shared/components/skeleton/skeleton.component';
import { CATEGORY_DROPDOWN_CONFIG } from '../../../../shared/constants/blog.constants';
import { DEFAULT_BLOG_FILTER } from '../../../../shared/constants/constants';
import { BlogFilter } from '../../../../shared/interface/interface';
import { Blog } from '../../../../shared/models/blog.model';
import { CategoryModel } from '../../../../shared/models/category.model';
import {
  DropDownConfig,
  DropDownOption,
} from '../../../../shared/models/multiselect-dropdown.model';
import { BlogsService } from '../../../../shared/services/blogs.service';
import { CategoryService } from '../../../../shared/services/category.service';
import { ScrollDisableService } from '../../../../shared/services/scroll-disable.service';
import { SharedModule } from '../../../../shared/shared.module';
import { BlogComponent } from '../blog/blog.component';

@Component({
  selector: 'app-blogs',
  standalone: true,
  templateUrl: './blogs.component.html',
  styleUrl: './blogs.component.scss',
  imports: [
    SharedModule,
    ConfirmationModalComponent,
    SkeletonLoaderComponent,
    BlogComponent,
    PageHeaderComponent,
    MultiselectDropdownComponent,
  ],
})
export class BlogsComponent implements OnInit, OnDestroy {
  blogList: Blog[] = [];
  deleteBlogId!: string | undefined;
  blogListLoading: boolean = false;
  subscription: Subscription = new Subscription();
  private blogFilterSubject$: BehaviorSubject<BlogFilter> =
    new BehaviorSubject<BlogFilter>(DEFAULT_BLOG_FILTER);
  totalBlog: number = 0;
  blogFilter = this.blogFilterSubject$.asObservable();
  menuOpenIndex = -1;
  deleteLoading: boolean = false;
  categoryDropdownSettings: DropDownConfig = {
    ...CATEGORY_DROPDOWN_CONFIG,
    title: '',
    required: false,
  };
  categoryForm = this.fb.group({
    category: [{ label: 'All', value: '' }],
  });
  categories: CategoryModel[] = [];
  constructor(
    private blogsService: BlogsService,
    private toastr: ToastrService,
    private fb: FormBuilder,
    private scrollDisableService: ScrollDisableService,
    private categoryService: CategoryService
  ) {}

  ngOnInit(): void {
    this.init();
  }
  init() {
    const sub = this.blogFilterSubject$.subscribe((value: BlogFilter) => {
      this.getBlogList(value);
    });
    this.subscription.add(sub);
    this.getCategoryList();
    this.subscribeToCategoryFormValueChange();
  }
  onDeleteBlog(id: string) {
    this.deleteBlogId = id;
    this.scrollDisableService.stopScroll();
  }

  deleteBlog() {
    if (this.deleteBlogId) {
      this.deleteLoading = true;
      this.blogsService.deleteBlog(this.deleteBlogId).subscribe({
        next: (res) => {
          this.deleteLoading = false;
          this.deleteBlogId = undefined;
          this.updateBlogFilter(DEFAULT_BLOG_FILTER);
          this.toastr.success('Blog deleted successfully.');
          this.scrollDisableService.addScroll();
        },
        error: (err) => {
          console.error(err);
          this.toastr.error(
            err?.error?.message || err?.message || 'Something went wrong!'
          );
          this.scrollDisableService.addScroll();
          this.deleteLoading = false;
        },
      });
    }
  }
  cancelDelete() {
    this.deleteBlogId = undefined;
    this.scrollDisableService.addScroll();
  }
  getBlogList(value: BlogFilter) {
    this.blogListLoading = true;
    this.blogsService.getBlogList(value).subscribe({
      next: (res: any) => {
        this.blogList =
          value.pageNumber > 1 ? [...this.blogList, ...res.result] : res.result;
        this.totalBlog = res.count;
        this.blogListLoading = false;
      },
      error: (err: any) => {
        console.error(err);
        this.toastr.error(
          err?.error?.message || err?.message || 'Something went wrong!'
        );
        this.blogListLoading = false;
      },
    });
  }

  loadMoreIfLastIndex() {
    const { searchText, pageNumber, limit, categorySlug } =
      this.blogFilterSubject$.value;
    if (pageNumber * limit < this.totalBlog) {
      this.updateBlogFilter({
        searchText,
        limit,
        categorySlug,
        pageNumber: pageNumber + 1,
      });
    }
  }

  updateBlogFilter(filter: BlogFilter) {
    this.blogFilterSubject$.next(filter);
  }
  closeAllMenu(index: number) {
    if (index === this.menuOpenIndex) {
      this.menuOpenIndex = -1;
    }
  }
  openMenu(index: number) {
    this.menuOpenIndex = index;
  }
  handleSearch(searchText: string) {
    if (searchText.length === 0 || searchText?.trim()) {
      this.updateBlogFilter({
        ...this.blogFilterSubject$.value,
        pageNumber: 1,
        searchText: searchText,
      });
    }
  }
  getCategoryList() {
    this.categoryService
      .getCategoryList({ pageNumber: 1, limit: 10000000, searchText: '' })
      .subscribe({
        next: (res) => {
          this.categories = res.result;
        },
        error: (err) => {
          this.toastr.error(
            err?.error?.message || err.message || 'Something went wrong!'
          );
        },
      });
  }

  subscribeToCategoryFormValueChange() {
    const sub = this.categoryForm.valueChanges.subscribe((value) => {
      this.updateBlogFilter({
        ...DEFAULT_BLOG_FILTER,
        categorySlug: (value?.category as any)?.value || '',
      });
    });
    this.subscription.add(sub);
  }
  get categoryOptions(): DropDownOption[] {
    return [
      { label: 'All', value: '' },
      ...this.categories.map((category) => ({
        label: category.name,
        value: category.slug || '',
      })),
    ];
  }
  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.scrollDisableService.addScroll();
  }
}
